/**
 * applyPendingLegalFormChange — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 4 refactor of the legal-form auto-apply block).
 *
 * Resolves the pending `company.pendingLegalFormChange` and applies it
 * when one of the two trigger windows fires :
 *   1. Manual deferred upgrade : applies on `day === plannedDay && month
 *      === plannedMonth && year === plannedYear` (mid-year OK).
 *   2. Auto threshold upgrade : applies on January 1st when `newYear ===
 *      plannedYear` (legacy behaviour, kept for back-compat).
 *
 * Returns the updated `company` (or the original if no change) and an
 * optional player-facing message describing the new statut juridique.
 * Helper is pure — the orchestrator handles the actual `pushCustomMessage`.
 *
 * @param {Object} state - current game state (unused but kept for forward
 *   compatibility / future inspection of audit history)
 * @param {Object} updatedCompany - the company object after monthly-report
 *   side effects ran (may carry a pendingLegalFormChange field)
 * @param {number} newDay
 * @param {number} newMonth
 * @param {number} newYear
 * @returns {{
 *   company: Object,
 *   message: Object | null,
 * }}
 */
import { LEGAL_FORMS } from '../../data/legalForms';

export function applyPendingLegalFormChange(state, updatedCompany, newDay, newMonth, newYear) {
  const cur = updatedCompany;
  if (!cur) return { company: cur, message: null };

  const pending = cur.pendingLegalFormChange;
  if (!pending) return { company: cur, message: null };

  const manualMatch = pending.manual === true
    && newDay === (pending.plannedDay || 1)
    && newMonth === pending.plannedMonth
    && newYear === pending.plannedYear;
  const autoMatch = !pending.manual
    && newMonth === 1
    && newYear === pending.plannedYear;
  if (!manualMatch && !autoMatch) return { company: cur, message: null };

  const newHistEntry = {
    from: cur.legalForm,
    to: pending.targetForm,
    atDay: (state?.totalDays || 0) + 1,
    reason: pending.reason || (manualMatch ? 'Évolution manuelle programmée' : 'Évolution automatique (seuil de CA dépassé)'),
  };

  const message = {
    senderType: 'urssaf',
    senderName: 'Centre des Impôts',
    tone: 'formal',
    kind: 'info',
    subject: `Bascule en ${LEGAL_FORMS[pending.targetForm]?.name || pending.targetForm}`,
    body: manualMatch
      ? `Le dossier de transformation est validé. Votre statut juridique est désormais ${LEGAL_FORMS[pending.targetForm]?.fullLabel} (régime ${pending.targetRegime}). K-bis mis à jour, cotisations sociales et conditions bancaires recalibrées.`
      : `Votre statut juridique a été basculé en ${LEGAL_FORMS[pending.targetForm]?.fullLabel} (régime ${pending.targetRegime}). Cotisations sociales et conditions bancaires mises à jour. Bonne nouvelle année !`,
    ttlDays: 30,
  };

  return {
    company: {
      ...cur,
      legalForm: pending.targetForm,
      fiscalRegime: pending.targetRegime,
      legalFormHistory: [...(cur.legalFormHistory || []), newHistEntry],
      pendingLegalFormChange: null,
    },
    message,
  };
}
